import { memo, PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectChartPanesConfig, selectChartSeriesGroups, selectChartScalesAndLinesSettings } from '../../../selectors/chart'
import ChartPaneActions from '../chartPaneActions'
import { createPortal } from 'react-dom'
import SeriesMetadataDisplay from '../seriesMetaDisplay/seriesMetadataDisplay'
import './chartPane.css'
import ChartContextMenuButton from '../chartContextMenuButton/chartContextMenuButton'
import ChartContextMenuHost, { ChartContextMenuAnchor, ChartContextMenuHostHandle } from '../chartContextMenuOptions/ChartContextMenuHost'
import { ViewType } from '../../../assets/constants'
import { TradeOptions } from '../../../reducer/scalper.reducer'
import PaneMetaContainer from './PaneMetaContainer'

export type ChartPaneProps = PropsWithChildren<{
    viewKey: string,
    id: string,
    index: number,
    primaryChart: boolean
    // config: PaneConfig,
    container: HTMLDivElement
    showLogo: boolean,
    tradeOptions?: TradeOptions,
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
    screenerChart: boolean

}>

const ChartPane: React.FC<ChartPaneProps> = ({viewKey, id, index, container, showLogo, screenerChart, primaryChart, tradeOptions, onSecuritySelected }) => {
    const [paneContainer, setPaneContainer] = useState<HTMLDivElement>();
    const panesConfig = useSelector(selectChartPanesConfig(viewKey));
    const seriesGroups = useSelector(selectChartSeriesGroups(viewKey))
    const scalesAndLinesSettings = useSelector(selectChartScalesAndLinesSettings(viewKey))
    const showPlusButton = scalesAndLinesSettings?.showPlusButton ?? true
    const pane = panesConfig[id]
    const contextMenuHostRef = useRef<ChartContextMenuHostHandle>(null)
    const openContextMenu = useCallback((anchor: ChartContextMenuAnchor) => {
        contextMenuHostRef.current?.open(anchor)
    }, [])

    const currentIndex = useRef(index)

    const updatePaneContainer = useCallback(() => {
        if (!container?.isConnected) {
            setPaneContainer(undefined)
            return
        }
        let child = container.querySelector<HTMLDivElement>(`.chart-pane-container`)
        if (child) {
            setPaneContainer(child)
        } else {
            const child = document.createElement('div')
            child.classList.add('chart-pane-container')
            const containerStyle: Record<string, string> = {
                display: 'flex',
                'flex-direction': 'column',
                'z-index': '3',
                'justify-content': 'start',
                'align-items': 'start',
                width: '100%'
            }

            for (let key in containerStyle) {
                child.style.setProperty(key, containerStyle[key]!)
            }
            container.appendChild(child)
            setPaneContainer(child)
        }

    }, [container])

    useEffect(() => {
        currentIndex.current = index
    }, [index])

    useLayoutEffect(() => {
        updatePaneContainer()
    }, [index, container, updatePaneContainer])


    return <>
            {paneContainer && pane && createPortal(<>
                <PaneMetaContainer>
                    {pane.seriesGroups.map((sgid) => (
                        <SeriesMetadataDisplay
                            tradeOptions={tradeOptions}
                            seriesGroup={seriesGroups[sgid]}
                            key={sgid}
                            screenerChart={screenerChart}
                            primaryChart={primaryChart}
                            onSecuritySelected={onSecuritySelected}
                            view={ViewType.CHART}
                        />
                    ))}
                </PaneMetaContainer>
                { !screenerChart ? <ChartPaneActions index={index} primary={pane.primary} /> : null }
                { !screenerChart && primaryChart && showPlusButton && (
                    <ChartContextMenuButton
                        onOpenContextMenu={openContextMenu}
                        paneContainer={paneContainer}
                        paneIndex={currentIndex.current}
                        viewKey={viewKey}
                    />
                )}
                { !screenerChart && primaryChart && (
                    <ChartContextMenuHost ref={contextMenuHostRef} viewKey={viewKey} />
                )}
            </>, paneContainer)}
            
        </>
}

export default memo(ChartPane)